// src/components/layout/Navbar.jsx
import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import Button from '../common/Button';
import logo from '../../assets/images/logo.png';
import "../../styles/Navbar.css";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Always close dropdown when auth state changes (prevents auto-open after login)
  useEffect(() => {
    setDropdownOpen(false);
  }, [user]);

  return (
    <nav className="navbar">
      <div className="navContainer">
        {/* Logo */}
        <Link to="/" className="navLogo">
          <img src={logo} alt="ArtInv" className="logoImg" />
          <span className="logoText">Art&amp;inv...</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="desktopNav">
          <div className="navLinks">
            <Link to="/" className="navLink">Home</Link>
            <Link to="/artworks" className="navLink">Artworks</Link>
            <Link to="/marketplace" className="navLink">Marketplace</Link>
            <Link to="/about" className="navLink">About</Link>
          </div>

          {/* User Actions */}
          <div className="navActions">
            {user ? (
              <div className="userDropdown" ref={dropdownRef}>
                <button 
                  className="userButton" 
                  onClick={toggleDropdown}
                  aria-expanded={dropdownOpen}
                >
                  <span className="userIcon">
                    <FontAwesomeIcon icon={faUser} />
                  </span>
                  <span className="userName">{user.email?.split('@')[0] || user.name || 'Account'}</span>
                  <span className={`dropdownIcon ${dropdownOpen ? 'rotate' : ''}`}>▼</span>
                </button>
                
                <div className={`dropdownMenu ${dropdownOpen ? 'open' : ''}`}>
                  <div className="dropdownHeader">
                    <span className="dropdownEmail">{user.email}</span>
                    <span className="dropdownRole">{user.role}</span>
                  </div>
                  <Link to="/profile" className="dropdownItem" onClick={() => setDropdownOpen(false)}>My Profile</Link>
                  <Link to="/settings" className="dropdownItem" onClick={() => setDropdownOpen(false)}>Settings</Link>
                  <div className="dropdownDivider"></div>
                  <Button variant="danger" size="sm" onClick={handleLogout} style={{ width: '100%' }}>
                    Logout
                  </Button>
                </div>
              </div>
            ) : (
              <div className="authButtons">
                <Link to="/login" className="loginBtn">Log in</Link>
                <Link to="/signup" className="signupBtn">Sign up</Link>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="mobileMenuButton" 
          onClick={toggleMobileMenu}
          aria-label="Toggle navigation menu"
        >
          <FontAwesomeIcon icon={mobileMenuOpen ? faTimes : faBars} />
        </button>
      </div>

      {/* Mobile Navigation */}
      <div className={`mobileNav ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="mobileNavLinks">
          <Link to="/" className="mobileNavLink" onClick={toggleMobileMenu}>Home</Link>
          <Link to="/artworks" className="mobileNavLink" onClick={toggleMobileMenu}>Artworks</Link>
          <Link to="/marketplace" className="mobileNavLink" onClick={toggleMobileMenu}>Marketplace</Link>
          <Link to="/about" className="mobileNavLink" onClick={toggleMobileMenu}>About</Link>
        </div>
        
        {user ? (
          <div className="mobileUserActions">
            <div className="mobileUserInfo">
              <span>{user.email}</span>
              <span className="mobileUserRole">{user.role}</span>
            </div>
            <Link to="/profile" className="mobileNavLink" onClick={toggleMobileMenu}>My Profile</Link>
            <Link to="/settings" className="mobileNavLink" onClick={toggleMobileMenu}>Settings</Link>
            <Button
              variant="danger"
              size="md"
              className="mobileLogoutBtn"
              onClick={() => { handleLogout(); toggleMobileMenu(); }}
              style={{ width: '100%', marginTop: '0.75rem' }}
            >
              Logout
            </Button>
          </div>
        ) : (
          <div className="mobileAuthButtons">
            <Link to="/login" className="mobileLoginBtn" onClick={toggleMobileMenu}>Log in</Link>
            <Link to="/signup" className="mobileSignupBtn" onClick={toggleMobileMenu}>Sign up</Link>
          </div>
        )}
      </div>
    </nav>
  );
}